import React from "react";
import { IconBin, IconCloseCircleFill, IconDownload, IconMenuVerticalDots } from "@synthesis/shared-icons";
import { useDownload, cn } from "@synthesis/shared-utils";
import { Button, Dropdown, FileIconComponent, Tooltip, setSnack, type DropdownScheme, type StateProps } from "@synthesis/shared-ui";

import {
  uploadedMetaToDownloadMeta,
  type FileListProps,
  type FileRowDropdownOptionItem,
  type UploadedFileMeta,
  type UploaderListItem,
} from "../uploader.lib";

type FileRowDropdownData = { scheme: DropdownScheme; state: StateProps };

type FileRowProps = {
  fileMeta: UploaderListItem;
  actions: FileRowDropdownOptionItem[];
  buildDropdownOptions: (fileMeta: UploaderListItem) => FileRowDropdownData;
};

const FileRow = React.memo<FileRowProps>(({ fileMeta, actions, buildDropdownOptions }) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const handleDropdownChange = React.useCallback((value: boolean) => {
    setIsDropdownOpen(value);
  }, []);

  const displayName = fileMeta instanceof File ? fileMeta.name : (fileMeta.name ?? `${fileMeta.baseName}.${fileMeta.ext}`);
  const ext = fileMeta instanceof File ? fileMeta.name.split(".").at(-1) : (fileMeta.ext ?? fileMeta.name?.split(".").at(-1));
  const { scheme: dropdownScheme, state: dropdownState } = buildDropdownOptions(fileMeta);

  return (
    <div className="group min-w-0 max-w-full overflow-x-hidden">
      <div
        className={cn(
          "outline-system-info-70 hover:bg-neutrals-95 focus:bg-neutrals-95 group-focus-within:bg-neutrals-95 flex max-w-full items-center gap-3 overflow-x-hidden rounded p-1 transition duration-75",
          isDropdownOpen && "bg-neutrals-95",
        )}
        tabIndex={0}
      >
        <FileIconComponent className="size-6 shrink-0" ext={ext} />
        <div className="min-w-0 flex-1 overflow-hidden">
          <Tooltip description={displayName}>
            <h3 className="text-system-info-50 truncate">{displayName}</h3>
          </Tooltip>
        </div>
        {actions.length > 1 && (
          <Dropdown
            size="sm"
            scheme={dropdownScheme}
            state={dropdownState}
            onOpenChange={handleDropdownChange}
            trigger={
              <Button
                theme="text"
                className={cn(
                  "ml-auto rounded-sm opacity-0 transition duration-75 hover:opacity-100 focus:opacity-100 group-hover:opacity-100 group-focus:opacity-100",
                  isDropdownOpen && "!opacity-100",
                )}
                size="xs"
              >
                <IconMenuVerticalDots className="size-4" />
              </Button>
            }
          />
        )}
        {actions.length === 1 &&
          (actions[0]?.type === "delete" ? (
            <Button
              className="text-type-minor ml-auto hidden group-hover:inline-flex group-focus:inline-flex"
              theme="text"
              size="xs"
              rightIcon={<IconBin className="text-neutrals-60 ml-0.5 size-5" />}
              onClick={actions[0].callback}
            >
              Удалить
            </Button>
          ) : (
            <Button
              className="ml-auto hidden group-hover:inline-block group-focus:inline-block"
              theme="text"
              size="xs"
              icon={actions[0]?.label}
              onClick={actions[0]?.callback}
            />
          ))}
      </div>
    </div>
  );
});

export const FileList = React.memo<FileListProps>(({ files, onDeleteFile, onDownloadFile, fetchLoadedFile }) => {
  const [list, setList] = React.useState<UploaderListItem[]>([]);

  const download = useDownload();

  const downloadFile = React.useCallback(
    async (fileMeta: UploaderListItem) => {
      if (onDownloadFile) {
        await onDownloadFile(fileMeta);
        return;
      }

      if (!fetchLoadedFile || fileMeta instanceof File) {
        return;
      }
      try {
        const uploaded = fileMeta as UploadedFileMeta;
        const downloadMeta = uploadedMetaToDownloadMeta(uploaded);
        if (!downloadMeta) {
          setSnack({
            title: "Не удалось скачать файл",
            description: "У вложения нет идентификатора или имени для сохранения.",
            icon: <IconCloseCircleFill className="size-4" />,
          });
          return;
        }
        const blob = await fetchLoadedFile(uploaded);
        download(downloadMeta, blob as BlobPart);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        setSnack({ title: "Не удалось скачать файл", description: message, icon: <IconCloseCircleFill className="size-4" /> });
      }
    },
    [download, fetchLoadedFile, onDownloadFile],
  );

  React.useEffect(() => {
    if (files?.length) {
      setList(files);
    }
  }, [files]);

  const buildActions = React.useCallback(
    (fileMeta: UploaderListItem): FileRowDropdownOptionItem[] => {
      const actions: FileRowDropdownOptionItem[] = [];
      const canDownloadByLegacyHandler = fetchLoadedFile && !(fileMeta instanceof File) && uploadedMetaToDownloadMeta(fileMeta as UploadedFileMeta);

      if (onDownloadFile || canDownloadByLegacyHandler) {
        actions.push({
          type: "download",
          label: <IconDownload className="text-type-light size-5" />,
          callback: () => void downloadFile(fileMeta),
        });
      }

      if (onDeleteFile) {
        actions.push({
          type: "delete",
          label: <IconBin className="text-type-light size-5" />,
          callback: () => onDeleteFile(fileMeta),
        });
      }

      return actions;
    },
    [downloadFile, fetchLoadedFile, onDeleteFile, onDownloadFile],
  );

  const buildDropdownOptions = React.useCallback(
    (fileMeta: UploaderListItem): FileRowDropdownData => {
      const actions = buildActions(fileMeta);
      const elements: DropdownScheme[number]["elements"] = [];
      const state: StateProps = {};

      if (actions[0]) {
        const { callback } = actions[0];
        elements.push({
          id: "download",
          value: "download",
          label: (
            <div className="flex items-center gap-2 text-base font-medium">
              <IconDownload className="size-4" />
              <div>Скачать файл</div>
            </div>
          ),
        });
        state.download = () => callback();
      }

      if (actions[1]) {
        const { callback } = actions[1];
        elements.push({
          id: "delete",
          value: "delete",
          label: (
            <div className="flex items-center gap-2 text-base font-medium text-red-700">
              <IconBin className="size-4" />
              <div>Удалить из списка</div>
            </div>
          ),
        });
        state.delete = () => callback();
      }

      return { scheme: [{ type: "ITEMS", elements }], state };
    },
    [buildActions],
  );

  if (!files?.length) {
    return null;
  }

  return (
    <section className="flex w-full max-w-full flex-col gap-1 overflow-x-hidden rounded-sm">
      {list.map((fileMeta) => (
        <FileRow
          key={
            fileMeta instanceof File
              ? `${fileMeta.name}-${fileMeta.lastModified}`
              : (fileMeta.id ?? fileMeta.name ?? `${fileMeta.baseName}.${fileMeta.ext}`)
          }
          fileMeta={fileMeta}
          actions={buildActions(fileMeta)}
          buildDropdownOptions={buildDropdownOptions}
        />
      ))}
    </section>
  );
});
